import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { FaTrash, FaUpload } from "react-icons/fa";
import axiosInstance from "../components/api/axiosInstance";
import { resolveImageUrl } from "../config/apiConfig";
import { canDeleteInPortal } from "./portalSession";

function formatDateTime(value) {
  if (!value) return "—";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function fileUrl(attachment) {
  const raw = attachment?.File_Url || attachment?.File_Path;
  if (!raw) return null;
  return resolveImageUrl(raw);
}

function entityLabel(attachment) {
  if (attachment.Entity_Type === "project") {
    const parts = [attachment.Entity_Code, attachment.Entity_Name].filter(Boolean);
    return parts.length ? parts.join(" — ") : "Project";
  }
  if (attachment.Entity_Type === "service") {
    return attachment.Entity_Name || "Service";
  }
  return "—";
}

const EMPTY_UPLOAD_FORM = {
  linkType: "project",
  projectId: "",
  serviceId: "",
  remarks: "",
};

export default function PortalClientAttachments({
  tenantClientId,
  scope,
  projects = [],
  services = [],
}) {
  const fileInputRef = useRef(null);
  const [attachments, setAttachments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [filterEntityId, setFilterEntityId] = useState("");
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [uploadForm, setUploadForm] = useState(EMPTY_UPLOAD_FORM);
  const [uploadError, setUploadError] = useState("");
  const [uploading, setUploading] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const canDelete = useMemo(() => canDeleteInPortal(), []);

  const isProjectScope = scope === "project";
  const entities = isProjectScope ? projects : services;
  const canUpload = entities.length > 0;
  const sectionTitle = isProjectScope ? "Project attachments" : "Service attachments";
  const emptyEntitiesMessage = isProjectScope
    ? "Add a project to upload attachments."
    : "Add a service to upload attachments.";
  const filterLabel = isProjectScope ? "Filter by project" : "Filter by service";
  const filterAllLabel = isProjectScope ? "All projects" : "All services";
  const filterEmptyMessage = isProjectScope
    ? "No attachments for this project."
    : "No attachments for this service.";

  const loadAttachments = useCallback(async () => {
    if (!tenantClientId) return;
    setLoading(true);
    setError("");
    try {
      const params = {};
      if (filterEntityId) {
        if (isProjectScope) params.project_id = filterEntityId;
        else params.service_id = filterEntityId;
      }
      const res = await axiosInstance.get(
        `/client/tenant-clients/${tenantClientId}/attachments`,
        { params }
      );
      let rows = Array.isArray(res.data?.data) ? res.data.data : [];
      if (!filterEntityId) {
        rows = rows.filter((row) => row.Entity_Type === scope);
      }
      setAttachments(rows);
    } catch (err) {
      setAttachments([]);
      setError(err.response?.data?.message || "Could not load attachments.");
    } finally {
      setLoading(false);
    }
  }, [tenantClientId, filterEntityId, isProjectScope, scope]);

  useEffect(() => {
    loadAttachments();
  }, [loadAttachments]);

  const openUploadModal = () => {
    setUploadForm({
      linkType: isProjectScope ? "project" : "service",
      projectId: "",
      serviceId: "",
      remarks: "",
    });
    setUploadError("");
    setShowUploadModal(true);
  };

  const closeUploadModal = () => {
    setShowUploadModal(false);
    setUploadForm(EMPTY_UPLOAD_FORM);
    setUploadError("");
  };

  const setUploadField = (name, value) => {
    setUploadForm((f) => ({ ...f, [name]: value }));
  };

  const handleUploadSubmit = async (e) => {
    e.preventDefault();
    const file = fileInputRef.current?.files?.[0];
    if (!file) {
      setUploadError("File is required.");
      return;
    }

    const isProject = uploadForm.linkType === "project";
    const entityId = isProject ? uploadForm.projectId : uploadForm.serviceId;
    if (!entityId) {
      setUploadError(isProject ? "Project is required." : "Service is required.");
      return;
    }

    const entityType = isProject ? "projects" : "services";
    const basePath = `/client/tenant-clients/${tenantClientId}/${entityType}/${entityId}/attachments`;

    setUploading(true);
    setUploadError("");
    try {
      const formData = new FormData();
      formData.append("file", file);
      if (uploadForm.remarks.trim()) formData.append("remarks", uploadForm.remarks.trim());
      await axiosInstance.post(basePath, formData);
      if (fileInputRef.current) fileInputRef.current.value = "";
      closeUploadModal();
      await loadAttachments();
    } catch (err) {
      setUploadError(err.response?.data?.message || "Could not upload file.");
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (attachment) => {
    if (!window.confirm(`Delete ${attachment.File_Name}?`)) return;

    const entityType = attachment.Entity_Type === "project" ? "projects" : "services";
    const entityId = attachment.Entity_Id;
    const basePath = `/client/tenant-clients/${tenantClientId}/${entityType}/${entityId}/attachments`;

    setDeletingId(attachment.Tenant_Client_Attachment_Id);
    setError("");
    try {
      await axiosInstance.delete(`${basePath}/${attachment.Tenant_Client_Attachment_Id}`);
      await loadAttachments();
    } catch (err) {
      setError(err.response?.data?.message || "Could not delete attachment.");
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <>
      <div className="cportal-card" style={{ marginTop: 16 }}>
        <div className="cportal-card-head">
          <h2>{sectionTitle}</h2>
          {canUpload && (
            <button
              type="button"
              className="cportal-btn cportal-btn-primary"
              onClick={openUploadModal}
            >
              <FaUpload style={{ marginRight: 6 }} />
              Upload attachment
            </button>
          )}
        </div>

        {!canUpload ? (
          <p className="cportal-muted">{emptyEntitiesMessage}</p>
        ) : (
          <>
            <div className="cportal-field cportal-attachments-filter" style={{ maxWidth: 420 }}>
              <label htmlFor={`attachment-filter-${scope}`}>{filterLabel}</label>
              <select
                id={`attachment-filter-${scope}`}
                value={filterEntityId}
                onChange={(e) => setFilterEntityId(e.target.value)}
              >
                <option value="">{filterAllLabel}</option>
                {isProjectScope
                  ? projects.map((project) => (
                      <option key={project.Tenant_Project_Id} value={project.Tenant_Project_Id}>
                        {[project.Short_Code, project.Project_Name].filter(Boolean).join(" — ")}
                      </option>
                    ))
                  : services.map((service) => (
                      <option
                        key={service.Tenant_Client_Service_Id}
                        value={service.Tenant_Client_Service_Id}
                      >
                        {service.Service_Name}
                      </option>
                    ))}
              </select>
            </div>

            {error && <div className="cportal-alert cportal-alert-error">{error}</div>}

            {loading ? (
              <p className="cportal-muted">Loading attachments…</p>
            ) : attachments.length === 0 ? (
              <p className="cportal-muted">
                {filterEntityId ? filterEmptyMessage : "No attachments yet."}
              </p>
            ) : (
              <div className="cportal-table-wrap">
                <table className="cportal-table cportal-table--compact">
                  <thead>
                    <tr>
                      <th>{isProjectScope ? "Project" : "Service"}</th>
                      <th>File</th>
                      <th>Remarks</th>
                      <th>Uploaded</th>
                      {canDelete && <th className="cportal-th-actions">Actions</th>}
                    </tr>
                  </thead>
                  <tbody>
                    {attachments.map((attachment) => {
                      const url = fileUrl(attachment);
                      return (
                        <tr key={attachment.Tenant_Client_Attachment_Id}>
                          <td>{entityLabel(attachment)}</td>
                          <td>
                            {url ? (
                              <a href={url} target="_blank" rel="noopener noreferrer">
                                {attachment.File_Name}
                              </a>
                            ) : (
                              attachment.File_Name
                            )}
                          </td>
                          <td>{attachment.Remarks || "—"}</td>
                          <td>{formatDateTime(attachment.Uploaded_On)}</td>
                          {canDelete && (
                            <td>
                              <div className="cportal-re-actions">
                                <button
                                  type="button"
                                  className="re-lead-action re-lead-action--delete"
                                  onClick={() => handleDelete(attachment)}
                                  disabled={deletingId === attachment.Tenant_Client_Attachment_Id}
                                  title="Delete attachment"
                                  aria-label="Delete attachment"
                                >
                                  {deletingId === attachment.Tenant_Client_Attachment_Id ? (
                                    "…"
                                  ) : (
                                    <FaTrash size={16} />
                                  )}
                                </button>
                              </div>
                            </td>
                          )}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}
      </div>

      {showUploadModal && (
        <div className="cportal-modal-overlay" onClick={closeUploadModal} role="presentation">
          <div
            className="cportal-modal cportal-modal--form"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-labelledby={`upload-attachment-modal-title-${scope}`}
          >
            <div className="cportal-modal-header">
              <h3 id={`upload-attachment-modal-title-${scope}`}>Upload attachment</h3>
              <button type="button" onClick={closeUploadModal} aria-label="Close">
                ✕
              </button>
            </div>
            <form
              className="cportal-modal-body cportal-upload-attachment-form"
              onSubmit={handleUploadSubmit}
              noValidate
            >
              {uploadError && (
                <div className="cportal-alert cportal-alert-error">{uploadError}</div>
              )}

              {isProjectScope && projects.length > 0 && (
                <div className="cportal-field">
                  <label htmlFor={`upload-project-id-${scope}`}>
                    Project <span className="cportal-required">*</span>
                  </label>
                  <select
                    id={`upload-project-id-${scope}`}
                    value={uploadForm.projectId}
                    onChange={(e) => setUploadField("projectId", e.target.value)}
                    disabled={uploading}
                    required
                  >
                    <option value="">Select project</option>
                    {projects.map((project) => (
                      <option key={project.Tenant_Project_Id} value={project.Tenant_Project_Id}>
                        {[project.Short_Code, project.Project_Name].filter(Boolean).join(" — ")}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              {!isProjectScope && services.length > 0 && (
                <div className="cportal-field">
                  <label htmlFor={`upload-service-id-${scope}`}>
                    Service <span className="cportal-required">*</span>
                  </label>
                  <select
                    id={`upload-service-id-${scope}`}
                    value={uploadForm.serviceId}
                    onChange={(e) => setUploadField("serviceId", e.target.value)}
                    disabled={uploading}
                    required
                  >
                    <option value="">Select service</option>
                    {services.map((service) => (
                      <option
                        key={service.Tenant_Client_Service_Id}
                        value={service.Tenant_Client_Service_Id}
                      >
                        {service.Service_Name}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              <div className="cportal-field">
                <label htmlFor={`upload-file-${scope}`}>
                  File <span className="cportal-required">*</span>
                </label>
                <input
                  ref={fileInputRef}
                  id={`upload-file-${scope}`}
                  type="file"
                  disabled={uploading}
                  accept=".png,.jpg,.jpeg,.gif,.webp,.pdf,.doc,.docx,.xls,.xlsx,.txt,.csv,.md"
                  required
                />
              </div>

              <div className="cportal-field">
                <label htmlFor={`upload-remarks-${scope}`}>Remarks (optional)</label>
                <textarea
                  id={`upload-remarks-${scope}`}
                  rows={3}
                  value={uploadForm.remarks}
                  onChange={(e) => setUploadField("remarks", e.target.value)}
                  placeholder="Add a note about this file (optional)"
                  disabled={uploading}
                />
              </div>

              <div className="cportal-form-actions">
                <button
                  type="button"
                  className="cportal-btn cportal-btn-ghost"
                  onClick={closeUploadModal}
                  disabled={uploading}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="cportal-btn cportal-btn-primary"
                  disabled={uploading}
                >
                  {uploading ? "Uploading…" : "Upload"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </>
  );
}
